import { motion } from "motion/react";
import { Scale, ArrowDownToLine, GitFork, Scissors, Activity, ChevronRight } from "lucide-react";
import type { StrainGraph } from "@/types";
import { computeCruxes } from "@/lib/graph";
import { CRUX_CATEGORIES } from "@/lib/vocabulary";
import { useStore } from "@/store/useStore";
import { Badge } from "@/components/Badge";

export function CruxView({ graph }: { graph: StrainGraph }) {
  const cruxes = computeCruxes(graph).slice(0, 8);
  const store = useStore();
  const maxScore = Math.max(1, ...cruxes.map((c) => c.score));

  return (
    <div className="h-full overflow-y-auto bg-grid-fine">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="mb-6">
          <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)] mb-1">crux view</div>
          <h2 className="text-lg font-semibold">Where does this idea hinge?</h2>
          <p className="text-[13px] text-[var(--color-ink-muted)] mt-1 leading-relaxed">
            The points where the strain is most likely to change, split, or fail. Ranked by downstream dependency,
            undercuts, fork divergence, and belief-change reports.
          </p>
        </div>

        <div className="space-y-3">
          {cruxes.map((c, i) => (
            <motion.div
              key={c.node_id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 hover:border-[var(--color-secondary)] transition-colors cursor-pointer"
              onClick={() => { store.setExplorerMode("canvas"); store.selectNode(c.node_id); }}
            >
              <div className="flex items-start gap-3">
                {/* rank + score bar */}
                <div className="shrink-0 flex flex-col items-center gap-1">
                  <div className="w-8 h-8 rounded-lg grid place-items-center bg-[rgba(251,191,36,0.1)] border border-[rgba(251,191,36,0.3)] text-[var(--color-secondary)] font-mono text-sm font-semibold">
                    {i + 1}
                  </div>
                  <div className="w-1.5 h-16 rounded-full bg-[var(--color-surface-3)] overflow-hidden flex flex-col-reverse">
                    <div className="bg-[var(--color-secondary)]" style={{ height: `${(c.score / maxScore) * 100}%` }} />
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
                    <Badge tone="amber" icon={<Scale size={10} />}>crux</Badge>
                    <Badge tone="muted">{CRUX_CATEGORIES[c.category]}</Badge>
                  </div>

                  <p className="text-[14px] text-[var(--color-ink)] leading-snug mb-2 font-serif">{c.node_text}</p>

                  <div className="rounded-lg bg-[rgba(251,191,36,0.05)] border border-[rgba(251,191,36,0.15)] p-2.5 mb-2.5">
                    <p className="text-[12px] text-[var(--color-ink-soft)] leading-relaxed">{c.reason}</p>
                  </div>

                  {/* crux signals */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <CruxSignal icon={<ArrowDownToLine size={11} />} value={c.affected_downstream} label="downstream" />
                    <CruxSignal icon={<Scissors size={11} />} value={c.undercuts} label="undercuts" tone="amber" />
                    <CruxSignal icon={<GitFork size={11} />} value={c.forks_diverging} label="forks" tone="primary" />
                    <CruxSignal icon={<Activity size={11} />} value={c.belief_change_count} label="minds moved" tone="inferred" />
                  </div>

                  {/* stance split */}
                  <div className="mt-2.5">
                    <div className="flex items-center justify-between text-[10px] font-mono text-[var(--color-ink-faint)] mb-1">
                      <span className="text-[var(--color-rebut)]">disagree {c.stance_split.disagree}%</span>
                      <span>stance split</span>
                      <span className="text-[var(--color-support)]">agree {c.stance_split.agree}%</span>
                    </div>
                    <div className="h-1.5 rounded-full overflow-hidden flex bg-[var(--color-surface-3)]">
                      <span className="bg-[var(--color-rebut)]" style={{ width: `${c.stance_split.disagree}%` }} />
                      <span className="bg-[var(--color-ink-faint)]" style={{ width: `${c.stance_split.unsure}%` }} />
                      <span className="bg-[var(--color-support)]" style={{ width: `${c.stance_split.agree}%` }} />
                    </div>
                  </div>

                  <div className="flex items-center gap-1 mt-3 text-[11px] text-[var(--color-primary)]">
                    Open in canvas <ChevronRight size={12} />
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-6 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface-2)]/50 p-4">
          <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)] mb-1.5">how crux score works</div>
          <p className="text-[12px] text-[var(--color-ink-muted)] leading-relaxed font-mono">
            downstream + 2×undercuts + 1.5×forks + 1.5×disagreement + 2×mind_changes + evidence_disputes
          </p>
          <p className="text-[12px] text-[var(--color-ink-faint)] mt-2 leading-relaxed">
            Not a fake-precise ranking — a way to surface where attention matters. The UI always explains the reason in plain language.
          </p>
        </div>
      </div>
    </div>
  );
}

function CruxSignal({ icon, value, label, tone }: { icon: React.ReactNode; value: number; label: string; tone?: string }) {
  const color = tone === "amber" ? "text-[var(--color-secondary)]"
    : tone === "primary" ? "text-[var(--color-primary)]"
    : tone === "inferred" ? "text-[var(--color-inferred)]"
    : "text-[var(--color-ink-muted)]";
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)]/50 p-2 text-center">
      <div className={`flex items-center justify-center gap-0.5 text-sm font-mono font-semibold ${color}`}>
        {icon}{value}
      </div>
      <div className="text-[9px] text-[var(--color-ink-faint)] uppercase tracking-wider mt-0.5">{label}</div>
    </div>
  );
}
